class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    if (!this.validateQuantity(line)) {
      return;
    }

    this.enableLoading(line);
    this.querySelectorAll(".quantity__button").forEach((button) =>
      button.classList.add("disabled")
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);

          // dispatch cart:error
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                source: this.dataset.source,
                productVariantId: items[line - 1].dataset.variantId || line,
                errors: parsedState.errors,
                message: parsedState.errors,
              },
            })
          );
          return;
        }

        // dispatch line-item:change for the modified element
        document.dispatchEvent(
          new CustomEvent("line-item:change", {
            detail: {
              lineItem: parsedState.items[line - 1] || null,
              cart: parsedState,
              sectionId: this.dataset.source,
            },
          })
        );

        this.classList.toggle("is-empty", parsedState.item_count === 0);

        document.dispatchEvent(
          new CustomEvent("cart:change", {
            detail: {
              cart: parsedState,
              sectionId: this.dataset.source,
            },
          })
        );
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
      })
      .catch(() => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        this.querySelectorAll(".quantity__button").forEach((button) =>
          button.classList.remove("disabled")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            detail: {
              source: this.dataset.source,
              productVariantId: items[line - 1].dataset.variantId || line,
              errors: window.cartStrings.error,
              message: window.cartStrings.error,
            },
          })
        );
      })
      .finally(() => {
        this.querySelectorAll(".quantity__button").forEach((button) =>
          button.classList.remove("disabled")
        );
        this.disableLoading(line);
      });
  }

  validateQuantity(line) {
    const items = this.querySelectorAll(".cart-item");
    const input = items[line - 1]?.querySelector(".quantity__input");

    if (!input) {
      return false;
    }

    return input.reportValidity();
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "change",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get("cart-discount-code")) {
  customElements.define(
    "cart-discount-code",
    class CartDiscountCode extends HTMLElement {
      constructor() {
        super();

        this.activeFetch = null;
        this.toggleContent = this.toggleContent.bind(this);
        this.applyDiscount = this.applyDiscount.bind(this);
        this.removeDiscount = this.removeDiscount.bind(this);
      }

      connectedCallback() {
        this.initRefs();
        this.form = this.querySelector("form");
        this.input = this.querySelector('input[name="discount"]');
        this.submitBtn = this.querySelector('button[type="submit"]');
        this.toggleButton = this.querySelector(".cart-discount-code__label");

        this.toggleButton?.addEventListener("click", this.toggleContent);
        this.form?.addEventListener("submit", this.applyDiscount);
        this.addEventListener("click", this.removeDiscount);
        this.syncExpandedState();
      }

      disconnectedCallback() {
        this.toggleButton?.removeEventListener("click", this.toggleContent);
        this.form?.removeEventListener("submit", this.applyDiscount);
        this.removeEventListener("click", this.removeDiscount);

        if (this.activeFetch) {
          this.activeFetch.abort();
          this.activeFetch = null;
        }
      }

      initRefs() {
        this.refs = {};

        this.querySelectorAll("[ref]").forEach((element) => {
          const name = element.getAttribute("ref");

          if (name && element instanceof HTMLElement) {
            this.refs[name] = element;
          }
        });
      }

      createAbortController() {
        if (this.activeFetch) {
          this.activeFetch.abort();
        }

        this.activeFetch = new AbortController();
        return this.activeFetch;
      }

      getSectionsToRender() {
        const sections = [];
        const cartItems = document.getElementById("main-cart-items");
        const cartFooter = document.getElementById("main-cart-footer");
        const cartLiveRegionText = document.getElementById(
          "cart-live-region-text"
        );
        const cartDrawer = document.getElementById("CartDrawer");
        const cartIconBubble = document.getElementById("cart-icon-bubble");

        if (cartItems?.dataset.id) {
          sections.push({
            id: "main-cart-items",
            section: cartItems.dataset.id,
            selector: ".js-contents",
          });
        }

        if (cartFooter?.dataset.id) {
          sections.push({
            id: "main-cart-footer",
            section: cartFooter.dataset.id,
            selector: ".js-contents",
          });
        }

        if (cartLiveRegionText) {
          sections.push({
            id: "cart-live-region-text",
            section: "cart-live-region-text",
            selector: ".shopify-section",
          });
        }

        if (cartDrawer) {
          sections.push({
            id: "CartDrawer",
            section: "cart-drawer",
            selector: ".drawer__inner",
          });
        }

        if (cartIconBubble) {
          sections.push({
            id: "cart-icon-bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          });
        }

        return sections;
      }

      toggleContent(event) {
        event.preventDefault();

        this.classList.toggle("is-open");
        this.syncExpandedState();
      }

      syncExpandedState() {
        if (!(this.toggleButton instanceof HTMLButtonElement)) {
          return;
        }

        this.toggleButton.setAttribute(
          "aria-expanded",
          this.classList.contains("is-open").toString()
        );
      }

      getSectionInnerHTML(html, selector) {
        const parsedDocument = new DOMParser().parseFromString(
          html,
          "text/html"
        );
        const source = parsedDocument.querySelector(selector);

        return source ? source.innerHTML : "";
      }

      getSourceSection(parsedState) {
        const sectionId = this.dataset.sectionId;

        if (!sectionId || !parsedState.sections?.[sectionId]) {
          return null;
        }

        const selector = sectionId === "cart-drawer" ? ".drawer__inner" : ".js-contents";

        return new DOMParser()
          .parseFromString(parsedState.sections[sectionId], "text/html")
          .querySelector(selector);
      }

      renderSections(parsedState) {
        this.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          const sectionHtml = parsedState.sections?.[section.section];

          if (!target || !sectionHtml) {
            return;
          }

          const replacement = this.getSectionInnerHTML(sectionHtml, section.selector);
          if (!replacement) {
            return;
          }

          const elementToReplace =
            target.querySelector(section.selector) || target;

          elementToReplace.innerHTML = replacement;
        });
      }

      setButtonLoadingState(isLoading) {
        if (!(this.submitBtn instanceof HTMLButtonElement)) {
          return;
        }

        this.submitBtn.classList.toggle("loading", isLoading);
        this.submitBtn.disabled = isLoading;
        this.submitBtn.setAttribute("aria-disabled", isLoading.toString());
      }

      applyDiscount(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!(this.input instanceof HTMLInputElement)) {
          return;
        }

        const discountCodeValue = this.input.value.trim();
        if (!discountCodeValue) {
          return;
        }

        const existingDiscounts = this.existingDiscounts();
        if (
          existingDiscounts.some(
            (code) => code.toUpperCase() === discountCodeValue.toUpperCase()
          )
        ) {
          return;
        }

        const sectionsToRender = this.getSectionsToRender();
        if (!sectionsToRender.length) {
          return;
        }

        const {
          cartDiscountError,
          cartDiscountErrorDiscountCode,
          cartDiscountErrorShipping,
        } = this.refs;

        cartDiscountError?.classList.add("hidden");
        cartDiscountErrorDiscountCode?.classList.add("hidden");
        cartDiscountErrorShipping?.classList.add("hidden");

        this.setButtonLoadingState(true);

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(","),
          sections: sectionsToRender.map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body,
          signal: this.createAbortController().signal,
        })
          .then((response) => response.text())
          .then((responseText) => {
            const parsedState = JSON.parse(responseText);

            if (
              !this.checkApplicableCode(
                parsedState.discount_codes,
                discountCodeValue
              )
            ) {
              return;
            }

            const sourceSection = this.getSourceSection(parsedState);
            if (
              sourceSection &&
              !this.checkShippingDiscountCode(
                parsedState.discount_codes,
                discountCodeValue,
                sourceSection
              )
            ) {
              return;
            }

            this.renderSections(parsedState);
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Failed to apply discount:", error);
            }
          })
          .finally(() => {
            this.activeFetch = null;
            this.setButtonLoadingState(false);
          });
      }

      removeDiscount(event) {
        if (!(event.target instanceof HTMLElement)) {
          return;
        }

        const pill = event.target.closest(".cart-discount-code__pill");
        if (!(pill instanceof HTMLLIElement)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const discountCode = pill.dataset.discountCode;
        if (!discountCode) {
          return;
        }

        const existingDiscounts = this.existingDiscounts();
        const updatedDiscounts = existingDiscounts.filter(
          (code) => code !== discountCode
        );
        const sectionsToRender = this.getSectionsToRender();

        if (!sectionsToRender.length) {
          return;
        }

        this.setButtonLoadingState(true);

        const body = JSON.stringify({
          discount: updatedDiscounts.join(","),
          sections: sectionsToRender.map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body,
          signal: this.createAbortController().signal,
        })
          .then((response) => response.text())
          .then((responseText) => {
            const parsedState = JSON.parse(responseText);
            this.renderSections(parsedState);
          })
          .catch((error) => {
            if (error.name !== "AbortError") {
              console.error("Failed to remove discount:", error);
            }
          })
          .finally(() => {
            this.activeFetch = null;
            this.setButtonLoadingState(false);
          });
      }

      handleDiscountError(type) {
        const {
          cartDiscountError,
          cartDiscountErrorDiscountCode,
          cartDiscountErrorShipping,
        } = this.refs;
        const target =
          type === "discount_code"
            ? cartDiscountErrorDiscountCode
            : cartDiscountErrorShipping;

        cartDiscountError?.classList.remove("hidden");
        target?.classList.remove("hidden");
      }

      existingDiscounts() {
        return Array.from(
          this.querySelectorAll(".cart-discount-code__pill")
        )
          .map((pill) =>
            pill instanceof HTMLLIElement ? pill.dataset.discountCode : null
          )
          .filter(Boolean);
      }

      checkApplicableCode(cartDiscountCodes, discountCodeValue) {
        if (
          cartDiscountCodes?.find(
            (discount) =>
              discount.code === discountCodeValue && discount.applicable === false
          )
        ) {
          this.input.value = "";
          this.handleDiscountError("discount_code");
          return false;
        }

        return true;
      }

      checkShippingDiscountCode(
        cartDiscountCodes,
        discountCodeValue,
        sourceSection
      ) {
        const discountCodes = Array.from(
          sourceSection.querySelectorAll(".cart-discount-code__pill")
        )
          .map((pill) =>
            pill instanceof HTMLLIElement ? pill.dataset.discountCode : null
          )
          .filter(Boolean);
        const existingDiscounts = this.existingDiscounts();

        if (
          discountCodes.length === existingDiscounts.length &&
          discountCodes.every((code) => existingDiscounts.includes(code)) &&
          cartDiscountCodes?.find(
            (discount) =>
              discount.code === discountCodeValue && discount.applicable === true
          )
        ) {
          this.handleDiscountError("shipping");
          this.input.value = "";
          return false;
        }

        return true;
      }
    }
  );
}
